import React, {useEffect, useCallback, useRef} from 'react';
import { useLegendState as useState } from "@/hooks/useLegendState";
import {
    FolderOpen,
    LogOut,
    AlertCircle,
    Loader2,
    ExternalLink,
    FileText,
    CheckCircle2,
    XCircle,
    FolderIcon,
    Files
} from 'lucide-react';
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card.tsx";
import {Button} from "@/components/ui/button";
import {Progress} from "@/components/ui/progress";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@/components/ui/tabs";
import {Checkbox} from "@/components/ui/checkbox";
import {sourcesService} from "@/services/sourcesService";
import {documentPersistenceService} from "@/services/documentPersistenceService";
import {extractTextFromBlob} from "@/utils/textExtractionHelper";
import {
    GOOGLE_CONFIG,
    GOOGLE_DRIVE_SCOPES,
    STORAGE_KEY_EXPIRY,
    clearToken,
    fetchUserProfile,
    getStoredProfile,
    getStoredToken,
    saveToken,
    validateToken,
    ensureGoogleAccessToken,
    revokeAndClear,
} from "@/lib/googleAuth";
import { isTextBasedDocument } from "@/lib/isTextBasedDocument";

const toast = ({title, description, variant}: { title: string; description: string; variant?: string }) => {
    console.log(`[Toast ${variant || 'info'}]: ${title} - ${description}`);
};

const useConfigState = (initialConfig: any) => {
    const [config, setConfig] = useState(initialConfig);
    const setConfigField = useCallback((field: string, value: any) => {
        setConfig((prev: any) => ({...prev, [field]: value}));
    }, [setConfig]);
    return [config, setConfigField] as const;
};

interface DriveFile {
    id: string;
    name: string;
    mimeType: string;
    parents?: string[];
    folderPath?: string;
}

interface ImportProgress {
    total: number;
    completed: number;
    current: string;
    failed: string[];
}

export interface PersistenceContext {
    threadId?: string | null;
    spaceId?: string | null;
    topicId?: string | null;
    folderId?: string | null;
}

export interface PersistedFileResult {
    sourceId: string;
    storageId?: string;
    filename: string;
    url: string;
    folderPath?: string;
}

interface GoogleDrivePickerProps {
    onFilesSelected?: (files: Array<{ id: string; filename: string; url: string; folderPath?: string }> | Array<{ filename: string; text: string; mimeType: string; error?: string; folderPath?: string }>) => void;
    onFilesPersisted?: (files: PersistedFileResult[]) => void;
    onPickerStateChange?: (isOpen: boolean) => void;
    multiple?: boolean;
    value?: any;
    children?: React.ReactNode;
    isFinalConfirmation?: boolean;
    extractTextOnly?: boolean;
    persistenceContext?: PersistenceContext;
    persistToDatabase?: boolean;
}

export const GoogleDrivePicker = React.memo(function MyComponent({
                                                                     onFilesSelected,
                                                                     onFilesPersisted,
                                                                     onPickerStateChange,
                                                                     children,
                                                                     isFinalConfirmation = false,
                                                                     extractTextOnly = false,
                                                                     persistenceContext,
                                                                     persistToDatabase = false
                                                                 }: GoogleDrivePickerProps) {
    const [config] = useConfigState(GOOGLE_CONFIG);
    const [pickerMode, setPickerMode] = useState<'files' | 'folder'>('files');

    const authTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [scriptsLoaded, setScriptsLoaded] = useState(false);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [_userProfile, setUserProfile] = useState<any>(null);
    const [pickerInited, setPickerInited] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const [selectedFiles, setSelectedFiles] = useState<Array<{
        id?: string;
        filename: string;
        url?: string;
        text?: string;
        mimeType?: string;
        googleFileId?: string;
        folderPath?: string;
        isExtracted?: boolean;
    }>>([]);

    const [importProgress, setImportProgress] = useState<ImportProgress | null>(null);
    const [isImporting, setIsImporting] = useState(false);
    const [calculatedWidth, setCalculatedWidth] = useState<number | null>(null);
    const childrenRef = useRef<HTMLDivElement | null>(null);
    const [isConfirmed, setIsConfirmed] = useState(false);

    // Debug: Log selectedFiles changes
    useEffect(() => {
        if (extractTextOnly && selectedFiles.length > 0) {
            console.log('Extracted files in selectedFiles:', selectedFiles.filter(f => f.isExtracted));
        }
    }, [selectedFiles, extractTextOnly]);

    useEffect(() => {
        return () => {
            if (onPickerStateChange) {
                onPickerStateChange(false);
            }
        };
    }, [onPickerStateChange]);

    // Calculate width dynamically when children are passed
    useEffect(() => {
        if (children && childrenRef.current) {
            let resizeTimeout: ReturnType<typeof setTimeout> | null = null;

            const calculateWidth = () => {
                if (childrenRef.current) {
                    // Use getBoundingClientRect for more accurate measurement
                    const rect = childrenRef.current.getBoundingClientRect();
                    const width = rect.width * 1.5;
                    if (width > 0) {
                        setCalculatedWidth(width);
                    }
                }
            };

            // Calculate on mount with a small delay to ensure DOM is ready
            const initialTimeout = setTimeout(calculateWidth, 0);

            // Recalculate on window resize
            const handleResize = () => {
                if (resizeTimeout) clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(calculateWidth, 100);
            };
            
            window.addEventListener('resize', handleResize);
            const resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(childrenRef.current);

            return () => {
                clearTimeout(initialTimeout);
                if (resizeTimeout) clearTimeout(resizeTimeout);
                window.removeEventListener('resize', handleResize);
                resizeObserver.disconnect();
            };
        } else {
            setCalculatedWidth(null);
        }
    }, [children]);

    useEffect(() => {
        const loadScript = (src: string) => {
            return new Promise<void>((resolve, reject) => {
                if (document.querySelector(`script[src="${src}"]`)) {
                    resolve();
                    return;
                }
                const script = document.createElement('script');
                script.src = src;
                script.async = true;
                script.defer = true;
                script.onload = () => resolve();
                script.onerror = reject;
                document.body.appendChild(script);
            });
        };

        Promise.all([
            loadScript('https://apis.google.com/js/api.js'),
            loadScript('https://accounts.google.com/gsi/client'),
        ])
            .then(async () => {
                setScriptsLoaded(true);

                const savedToken = getStoredToken();
                const savedProfile = getStoredProfile();

                if (savedToken) {
                    const expiryStr = localStorage.getItem(STORAGE_KEY_EXPIRY);
                    const expiryTime = expiryStr ? parseInt(expiryStr, 10) : 0;

                    if (expiryTime > Date.now()) {
                        setIsLoading(true);
                        const isValid = await validateToken(savedToken);

                        if (isValid) {
                            setAccessToken(savedToken);
                            if (savedProfile) {
                                setUserProfile(savedProfile);
                            }
                            if ((window as any).gapi) {
                                (window as any).gapi.load('picker', () => {
                                    setPickerInited(true);
                                });
                            }
                            toast({
                                title: "Session Restored",
                                description: "Your Google Drive session has been restored.",
                            });
                        } else {
                            clearToken();
                            toast({
                                title: "Session Expired",
                                description: "Please sign in again to continue.",
                                variant: "default",
                            });
                        }
                        setIsLoading(false);
                    } else {
                        setIsLoading(true);
                        const isValid = await validateToken(savedToken);

                        if (isValid) {
                            const newExpiryTime = Date.now() + (50 * 60 * 1000);
                            localStorage.setItem(STORAGE_KEY_EXPIRY, newExpiryTime.toString());
                            setAccessToken(savedToken);
                            if (savedProfile) {
                                setUserProfile(savedProfile);
                            }
                            if ((window as any).gapi) {
                                (window as any).gapi.load('picker', () => {
                                    setPickerInited(true);
                                });
                            }
                        } else {
                            clearToken();
                        }
                        setIsLoading(false);
                    }
                }
            })
            .catch((err) => {
                setError('Failed to load Google APIs. Check your network connection.');
                console.error(err);
            });
    }, []);

    const initializePickerApi = useCallback(() => {
        if (!scriptsLoaded || !config.apiKey) return;

        try {
            if ((window as any).gapi) {
                (window as any).gapi.load('picker', () => {
                    setPickerInited(true);
                });
            }
        } catch (err) {
            console.error("Error loading picker:", err);
            setError("Failed to initialize Picker API.");
        }
    }, [scriptsLoaded, config.apiKey]);

    const handleAuthClick = useCallback(() => {
        if (!scriptsLoaded) return;
        if (!config.clientId || !config.apiKey) {
            setError("Please configure your Client ID and API Key first.");
            return;
        }

        setIsLoading(true);
        setError(null);

        if (authTimeoutRef.current) {
            clearTimeout(authTimeoutRef.current);
        }

        let callbackFired = false;
        let focusHandler: (() => void) | null = null;

        focusHandler = () => {
            setTimeout(() => {
                if (!callbackFired) {
                    setTimeout(() => {
                        if (!callbackFired) {
                            setIsLoading(false);
                            if (authTimeoutRef.current) {
                                clearTimeout(authTimeoutRef.current);
                                authTimeoutRef.current = null;
                            }
                            if (focusHandler) {
                                window.removeEventListener('focus', focusHandler);
                            }
                        }
                    }, 1500);
                }
            }, 200);
        };

        window.addEventListener('focus', focusHandler);

        try {
            const tokenClient = (window as any).google.accounts.oauth2.initTokenClient({
                client_id: config.clientId,
                scope: GOOGLE_DRIVE_SCOPES,
                callback: async (response: any) => {
                    callbackFired = true;

                    if (authTimeoutRef.current) {
                        clearTimeout(authTimeoutRef.current);
                        authTimeoutRef.current = null;
                    }
                    if (focusHandler) {
                        window.removeEventListener('focus', focusHandler);
                    }

                    if (response.error !== undefined) {
                        console.error('Auth Error:', response);
                        setError(`Auth Error: ${response.error} - Check if your Origin is whitelisted.`);
                        setIsLoading(false);
                        return;
                    }

                    setAccessToken(response.access_token);
                    initializePickerApi();

                    try {
                        const userInfo = await fetchUserProfile(response.access_token);
                        setUserProfile(userInfo);
                        saveToken(response.access_token, userInfo);
                    } catch (e) {
                        console.warn("Could not fetch user profile", e);
                        saveToken(response.access_token, null);
                    }

                    setIsLoading(false);
                },
            });

            tokenClient.requestAccessToken();

        } catch (err) {
            console.error(err);
            setError("Failed to initialize auth client. Check console for details.");
            setIsLoading(false);

            if (authTimeoutRef.current) {
                clearTimeout(authTimeoutRef.current);
                authTimeoutRef.current = null;
            }
            if (focusHandler) {
                window.removeEventListener('focus', focusHandler);
            }
        }
    }, [scriptsLoaded, config.clientId, config.apiKey, initializePickerApi]);

    const listFolderContents = useCallback(async (
        folderId: string,
        folderPath: string = ''
    ): Promise<DriveFile[]> => {
        if (!accessToken) return [];

        const allFiles: DriveFile[] = [];
        let pageToken: string | undefined;

        do {
            const params = new URLSearchParams({
                q: `'${folderId}' in parents and trashed = false`,
                fields: 'nextPageToken, files(id, name, mimeType, parents)',
                pageSize: '100',
            });
            if (pageToken) {
                params.append('pageToken', pageToken);
            }

            const response = await fetch(
                `https://www.googleapis.com/drive/v3/files?${params.toString()}`,
                {
                    headers: {Authorization: `Bearer ${accessToken}`},
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to list folder contents: ${response.statusText}`);
            }

            const data = await response.json();

            for (const file of data.files || []) {
                const filePath = folderPath ? `${folderPath}/${file.name}` : file.name;

                if (file.mimeType === 'application/vnd.google-apps.folder') {
                    // Recursively scan subfolders
                    const subFiles = await listFolderContents(file.id, filePath);
                    allFiles.push(...subFiles);
                } else {
                    // Only include text-based documents
                    if (isTextBasedDocument(file.mimeType, file.name)) {
                        allFiles.push({
                            ...file,
                            folderPath: folderPath,
                        });
                    }
                    // Skip non-text files silently
                }
            }

            pageToken = data.nextPageToken;
        } while (pageToken);

        return allFiles;
    }, [accessToken]);

    const getFileBlob = useCallback(async (file: DriveFile, forTextExtraction: boolean = false) => {
        const googleWorkspaceMimeTypes: Record<string, string> = {
            'application/vnd.google-apps.document': forTextExtraction ? 'text/plain' : 'application/pdf',
            'application/vnd.google-apps.spreadsheet': forTextExtraction ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.google-apps.presentation': forTextExtraction ? 'text/plain' : 'application/pdf',
            'application/vnd.google-apps.drawing': 'image/png',
            'application/vnd.google-apps.photo': 'image/jpeg',
        };

        let url;

        if (file.mimeType.startsWith('application/vnd.google-apps.')) {
            const exportMimeType = googleWorkspaceMimeTypes[file.mimeType] || (forTextExtraction ? 'text/plain' : 'application/pdf');
            url = `https://www.googleapis.com/drive/v3/files/${file.id}/export?mimeType=${encodeURIComponent(exportMimeType)}`;
        } else {
            url = `https://www.googleapis.com/drive/v3/files/${file.id}?alt=media`;
        }

        const response = await fetch(url, {
            headers: {Authorization: `Bearer ${accessToken}`},
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch file: ${response.statusText}`);
        }

        return await response.blob();
    }, [accessToken]);

    const importFolderFiles = useCallback(async (files: DriveFile[], folderName: string) => {
        setIsImporting(true);
        setImportProgress({
            total: files.length,
            completed: 0,
            current: '',
            failed: [],
        });

        const uploadedFiles: Array<{ id: string; filename: string; url: string; folderPath?: string }> = [];
        const persistedFiles: PersistedFileResult[] = [];
        const extractedTexts: Array<{ filename: string; text: string; mimeType: string; error?: string }> = [];
        let skippedCount = 0;

        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];

                // Double-check that file is text-based (should already be filtered, but just in case)
                if (!isTextBasedDocument(file.mimeType, file.name)) {
                    skippedCount++;
                    setImportProgress(prev => prev ? {
                        ...prev,
                        completed: i + 1,
                    } : null);
                    continue;
                }

                setImportProgress(prev => prev ? {
                    ...prev,
                    current: file.name,
                    completed: i,
                } : null);

                try {
                    const blob = await getFileBlob(file, extractTextOnly);

                    if (!blob || !blob?.type) {
                        console.warn(`Skipping file ${file.name}: invalid blob or type`);
                        skippedCount++;
                        setImportProgress(prev => prev ? {
                            ...prev,
                            failed: [...prev.failed, file.name],
                        } : null);
                        continue;
                    }

                    if (extractTextOnly) {
                        // Extract text only, no upload
                        const extracted = await extractTextFromBlob(
                            blob,
                            file.name,
                            file.mimeType,
                            accessToken || undefined
                        );

                        if (extracted.error) {
                            console.warn(`Failed to extract text from ${file.name}: ${extracted.error}`);
                            skippedCount++;
                            setImportProgress(prev => prev ? {
                                ...prev,
                                completed: i + 1,
                                failed: [...prev.failed, file.name],
                            } : null);
                        } else if (extracted.text) {
                            const fullPath = file.folderPath ? `${folderName}/${file.folderPath}` : folderName;
                            extractedTexts.push({
                                filename: extracted.filename,
                                text: extracted.text,
                                mimeType: extracted.mimeType,
                            });
                            
                            // Add to selectedFiles for UI display
                            setSelectedFiles(prev => {
                                // Check if file already exists to avoid duplicates
                                const exists = prev.some(f => f.filename === extracted.filename && f.isExtracted);
                                if (exists) {
                                    return prev;
                                }
                                return [...prev, {
                                    filename: extracted.filename,
                                    text: extracted.text,
                                    mimeType: extracted.mimeType,
                                    folderPath: fullPath,
                                    isExtracted: true,
                                }];
                            });
                            
                            // Update progress
                            setImportProgress(prev => prev ? {
                                ...prev,
                                completed: i + 1,
                            } : null);
                        } else {
                            skippedCount++;
                            setImportProgress(prev => prev ? {
                                ...prev,
                                completed: i + 1,
                            } : null);
                        }
                    } else {
                        // Upload to server and persist to database
                        let finalFilename = file.name;
                        let finalMimeType = blob.type || file.mimeType || 'application/octet-stream';
                        const fullPath = file.folderPath ? `${folderName}/${file.folderPath}` : folderName;

                        if (persistToDatabase && persistenceContext) {
                            // NEW: Use documentPersistenceService to upload AND create Source entry
                            const persisted = await documentPersistenceService.uploadAndPersist(
                                blob,
                                finalFilename,
                                finalMimeType,
                                {
                                    threadId: persistenceContext.threadId,
                                    spaceId: persistenceContext.spaceId,
                                    topicId: persistenceContext.topicId,
                                    folderId: persistenceContext.folderId,
                                    workspaceId: persistenceContext.workspaceId,
                                },
                                {
                                    googleDriveFileId: file.id,
                                    folderPath: fullPath,
                                }
                            );

                            uploadedFiles.push({
                                id: persisted.storageId || persisted.sourceId,
                                filename: finalFilename,
                                url: persisted.url || "",
                                folderPath: fullPath,
                            });

                            persistedFiles.push({
                                sourceId: persisted.sourceId,
                                storageId: persisted.storageId,
                                filename: finalFilename,
                                url: persisted.url || "",
                                folderPath: fullPath,
                            });

                            // Add file to selectedFiles immediately
                            setSelectedFiles(prev => {
                                const exists = prev.some(f => f.id === persisted.sourceId || f.googleFileId === file.id);
                                if (exists) return prev;
                                return [...prev, {
                                    id: persisted.sourceId,
                                    filename: finalFilename,
                                    url: persisted.url,
                                    googleFileId: file.id,
                                    folderPath: fullPath,
                                }];
                            });
                        } else {
                            // Legacy: just upload to storage without database entry
                            const result = await sourcesService.uploadFile(
                                blob,
                                finalFilename,
                                finalMimeType
                            );

                            const fileData = {
                                id: result.id,
                                filename: result.filename,
                                url: result.url,
                                googleFileId: file.id,
                                folderPath: fullPath,
                            };

                            uploadedFiles.push({
                                id: result.id,
                                filename: result.filename,
                                url: result.url,
                                folderPath: fullPath,
                            });

                            // Add file to selectedFiles immediately
                            setSelectedFiles(prev => {
                                const exists = prev.some(f => f.id === result.id || f.googleFileId === file.id);
                                if (exists) return prev;
                                return [...prev, fileData];
                            });
                        }
                    }

                } catch (error: any) {
                    console.error(`Error processing file ${file.name}:`, error);
                    skippedCount++;
                    setImportProgress(prev => prev ? {
                        ...prev,
                        failed: [...prev.failed, file.name],
                    } : null);
                }
            }

            // Update progress to show completion
            setImportProgress(prev => prev ? {
                ...prev,
                completed: files.length,
                current: 'Complete!',
            } : null);

            setIsImporting(false);

            // Call onFilesSelected callback with results
            if (onFilesSelected) {
                if (extractTextOnly && extractedTexts.length > 0) {
                   // onFilesSelected(extractedTexts);
                } else if (!extractTextOnly && uploadedFiles.length > 0) {
                    onFilesSelected(uploadedFiles);
                }
            }

            // Call onFilesPersisted callback with persisted files
            if (onFilesPersisted && persistedFiles.length > 0) {
                onFilesPersisted(persistedFiles);
            }

            const message = extractTextOnly
                ? skippedCount > 0
                    ? `${extractedTexts.length} file(s) text extracted. ${skippedCount} file(s) skipped.`
                    : `${extractedTexts.length} file(s) text extracted from folder.`
                : skippedCount > 0
                    ? `${uploadedFiles.length} text-based document(s) imported. ${skippedCount} non-text file(s) skipped.`
                    : `${uploadedFiles.length} text-based document(s) imported from folder.`;
            
            toast({
                title: extractTextOnly ? "Text extracted" : "Folder imported",
                description: message,
            });

            // Clear progress after showing completion for 2 seconds
            setTimeout(() => {
                setImportProgress(null);
            }, 2000);

        } catch (error: any) {
            console.error('Error in importFolderFiles:', error);
            setError(`Failed to ${extractTextOnly ? 'extract text from' : 'import'} folder: ${error.message || 'Unknown error'}`);
            setIsImporting(false);
            setImportProgress(null);
            toast({
                title: extractTextOnly ? "Extraction failed" : "Import failed",
                description: `Failed to ${extractTextOnly ? 'extract text from' : 'import'} folder: ${error.message || 'Unknown error'}`,
                variant: "destructive",
            });
        }
    }, [accessToken, onFilesSelected, onFilesPersisted, getFileBlob, extractTextOnly, persistToDatabase, persistenceContext]);

    const handleOpenPicker = useCallback(async (mode: 'files' | 'folder' = 'files') => {
        if (!pickerInited) {
            setError("Picker not ready. Please wait a moment and try again.");
            return;
        }

        setIsLoading(true);
        setError(null);

        let token: string;
        try {
            token = await ensureGoogleAccessToken(accessToken);
            if (token !== accessToken) {
                setAccessToken(token);
            }
        } catch {
            clearToken();
            setAccessToken(null);
            setUserProfile(null);
            setError("Your session has expired. Please sign in again.");
            setIsLoading(false);
            toast({
                title: "Session Expired",
                description: "Please sign in again to continue.",
                variant: "default",
            });
            return;
        }

        const expiryTime = Date.now() + (50 * 60 * 1000);
        localStorage.setItem(STORAGE_KEY_EXPIRY, expiryTime.toString());
        setIsLoading(false);

        try {
            if (onPickerStateChange) {
                onPickerStateChange(true);
            }

            const pickerCallback = async (data: any) => {
                if (data.action === (window as any).google.picker.Action.PICKED) {
                    const docs = data.docs;

                    if (mode === 'folder') {
                        const folder = docs[0];
                        if (folder.mimeType === 'application/vnd.google-apps.folder') {
                            setIsLoading(true);
                            toast({
                                title: "Scanning folder",
                                description: `Scanning "${folder.name}" for files...`,
                            });

                            try {
                                const files = await listFolderContents(folder.id, '');

                                if (files.length === 0) {
                                    toast({
                                        title: "Empty folder",
                                        description: "No importable files found in this folder.",
                                        variant: "default",
                                    });
                                    setIsLoading(false);
                                    return;
                                }

                                toast({
                                    title: "Found files",
                                    description: `Found ${files.length} files. Starting import...`,
                                });

                                setIsLoading(false);
                                await importFolderFiles(files, folder.name);
                            } catch (err: any) {
                                console.error('Error scanning folder:', err);
                                setError(`Failed to scan folder: ${err.message}`);
                                setIsLoading(false);
                            }
                        }
                    } else {
                        setIsLoading(true);
                        const uploadedFiles: Array<{ id: string; filename: string; url: string }> = [];
                        const persistedFilesResult: PersistedFileResult[] = [];
                        const extractedTexts: Array<{ filename: string; text: string; mimeType: string; error?: string }> = [];
                        let skippedCount = 0;

                        for (const file of docs) {
                            // Filter to only text-based documents
                            if (!isTextBasedDocument(file.mimeType, file.name)) {
                                skippedCount++;
                                continue;
                            }

                            try {
                                const blob = await getFileBlob(file, extractTextOnly);

                                if (!blob || !blob?.type) {
                                    skippedCount++;
                                    toast({
                                        title: "Skipped",
                                        description: `"${file.name}" is not a text-based document or could not be processed.`,
                                        variant: "default",
                                    });
                                    continue;
                                }

                                if (extractTextOnly) {
                                    // Extract text only, no upload
                                    const extracted = await extractTextFromBlob(
                                        blob,
                                        file.name,
                                        file.mimeType,
                                        accessToken || undefined
                                    );

                                    if (extracted.error) {
                                        console.warn(`Failed to extract text from ${file.name}: ${extracted.error}`);
                                        skippedCount++;
                                        toast({
                                            title: "Extraction failed",
                                            description: `Failed to extract text from "${file.name}": ${extracted.error}`,
                                            variant: "destructive",
                                        });
                                    } else if (extracted.text) {
                                        extractedTexts.push({
                                            filename: extracted.filename,
                                            text: extracted.text,
                                            mimeType: extracted.mimeType,
                                        });
                                        
                                        // Add to selectedFiles for UI display
                                        setSelectedFiles(prev => {
                                            // Check if file already exists to avoid duplicates
                                            const exists = prev.some(f => f.filename === extracted.filename && f.isExtracted);
                                            if (exists) {
                                                return prev;
                                            }
                                            return [...prev, {
                                                filename: extracted.filename,
                                                text: extracted.text,
                                                mimeType: extracted.mimeType,
                                                isExtracted: true,
                                            }];
                                        });
                                    } else {
                                        skippedCount++;
                                    }
                                } else {
                                    // Upload to server and persist to database
                                    let finalFilename = file?.name;
                                    let finalMimeType = blob.type || file?.mimeType || 'application/octet-stream';

                                    if (persistToDatabase && persistenceContext) {
                                        // NEW: Use documentPersistenceService to upload AND create Source entry
                                        const persisted = await documentPersistenceService.uploadAndPersist(
                                            blob,
                                            finalFilename,
                                            finalMimeType,
                                            {
                                                threadId: persistenceContext.threadId,
                                                spaceId: persistenceContext.spaceId,
                                                topicId: persistenceContext.topicId,
                                                folderId: persistenceContext.folderId,
                                                workspaceId: persistenceContext.workspaceId,
                                            },
                                            {
                                                googleDriveFileId: file.id,
                                            }
                                        );

                                        uploadedFiles.push({
                                            id: persisted.storageId || persisted.sourceId,
                                            filename: finalFilename,
                                            url: persisted.url || "",
                                        });

                                        persistedFilesResult.push({
                                            sourceId: persisted.sourceId,
                                            storageId: persisted.storageId,
                                            filename: finalFilename,
                                            url: persisted.url || "",
                                        });

                                        setSelectedFiles(prev => [...prev, {
                                            id: persisted.sourceId,
                                            filename: finalFilename,
                                            url: persisted.url,
                                            googleFileId: file.id,
                                        }]);
                                    } else {
                                        // Legacy: just upload to storage without database entry
                                        const result = await sourcesService.uploadFile(
                                            blob,
                                            finalFilename,
                                            finalMimeType
                                        );

                                        uploadedFiles.push({
                                            id: result.id,
                                            filename: result.filename,
                                            url: result.url,
                                        });

                                        setSelectedFiles(prev => [...prev, {
                                            id: result.id,
                                            filename: result.filename,
                                            url: result.url,
                                            googleFileId: file.id,
                                        }]);
                                    }
                                }

                            } catch (error: any) {
                                console.error('Error processing file:', error);
                                skippedCount++;
                                toast({
                                    title: extractTextOnly ? "Extraction failed" : "Import failed",
                                    description: `Failed to ${extractTextOnly ? 'extract text from' : 'import'} ${file.name || 'file'}: ${error.message || 'Unknown error'}`,
                                    variant: "destructive",
                                });
                            }
                        }

                        setIsLoading(false);

                        // Call onFilesSelected callback with results
                        if (onFilesSelected) {
                            if (extractTextOnly && extractedTexts.length > 0) {
                               // onFilesSelected(extractedTexts);
                            } else if (!extractTextOnly && uploadedFiles.length > 0) {
                                onFilesSelected(uploadedFiles);
                            }
                        }

                        // Call onFilesPersisted callback with persisted files
                        if (onFilesPersisted && persistedFilesResult.length > 0) {
                            onFilesPersisted(persistedFilesResult);
                        }

                        if (extractTextOnly) {
                            if (extractedTexts.length > 0) {
                                const message = skippedCount > 0
                                    ? `${extractedTexts.length} file(s) text extracted. ${skippedCount} file(s) skipped.`
                                    : `${extractedTexts.length} file(s) text extracted from Google Drive.`;
                                
                                toast({
                                    title: "Text extracted",
                                    description: message,
                                });
                            } else if (skippedCount > 0) {
                                toast({
                                    title: "No text extracted",
                                    description: `All ${skippedCount} selected file(s) were skipped or failed to extract text.`,
                                    variant: "default",
                                });
                            }
                        } else {
                            if (uploadedFiles.length > 0) {
                                const message = skippedCount > 0
                                    ? `${uploadedFiles.length} text-based document(s) imported. ${skippedCount} non-text file(s) skipped.`
                                    : `${uploadedFiles.length} text-based document(s) imported from Google Drive.`;
                                
                                toast({
                                    title: "Files imported",
                                    description: message,
                                });
                            } else if (skippedCount > 0) {
                                toast({
                                    title: "No text documents found",
                                    description: `All ${skippedCount} selected file(s) were skipped as they are not text-based documents.`,
                                    variant: "default",
                                });
                            }
                        }
                    }
                } else if (data.action === (window as any).google.picker.Action.CANCEL) {
                    console.log('Picker cancelled');
                }

                setTimeout(() => {
                    if (onPickerStateChange) {
                        onPickerStateChange(false);
                    }
                }, 100);
            };

            let view;
            if (mode === 'folder') {
                // Use DOCS view to show both files and folders, but enable folder selection
                view = new (window as any).google.picker.DocsView((window as any).google.picker.ViewId.DOCS);
                view.setSelectFolderEnabled(true);
                // Optionally filter to show only text-based documents and folders
                // This helps users see what files are in the folder
                view.setMimeTypes(
                    "application/vnd.google-apps.folder," +
                    "application/pdf," +
                    "application/vnd.google-apps.document," +
                    "application/vnd.google-apps.spreadsheet," +
                    "application/vnd.google-apps.presentation," +
                    "application/msword," +
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
                    "application/vnd.ms-excel," +
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
                    "application/vnd.ms-powerpoint," +
                    "application/vnd.openxmlformats-officedocument.presentationml.presentation," +
                    "application/vnd.oasis.opendocument.text," +
                    "application/vnd.oasis.opendocument.spreadsheet," +
                    "application/vnd.oasis.opendocument.presentation," +
                    "text/plain,text/html,text/csv,text/markdown,text/xml," +
                    "application/json,application/xml"
                );
            } else {
                view = new (window as any).google.picker.View((window as any).google.picker.ViewId.DOCS);
                // Only allow text-based documents
                view.setMimeTypes(
                    "application/pdf," +
                    "application/vnd.google-apps.document," +
                    "application/vnd.google-apps.spreadsheet," +
                    "application/vnd.google-apps.presentation," +
                    "application/msword," +
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
                    "application/vnd.ms-excel," +
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
                    "application/vnd.ms-powerpoint," +
                    "application/vnd.openxmlformats-officedocument.presentationml.presentation," +
                    "application/vnd.oasis.opendocument.text," +
                    "application/vnd.oasis.opendocument.spreadsheet," +
                    "application/vnd.oasis.opendocument.presentation," +
                    "text/plain,text/html,text/csv,text/markdown,text/xml," +
                    "application/json,application/xml"
                );
            }

            const width = window.innerWidth;
            const height = window.innerHeight;

            const pickerBuilder = new (window as any).google.picker.PickerBuilder()
                .setAppId(config.appId)
                .setOAuthToken(token)
                .setDeveloperKey(config.apiKey)
                .addView(view)
                .setSize(width, height)
                .setOrigin(window.location.origin)
                .setCallback(pickerCallback);

            if (mode !== 'folder') {
                pickerBuilder.addView(new (window as any).google.picker.DocsUploadView());
            }

            const picker = pickerBuilder.build();
            picker.setVisible(true);

            const styleIframes = () => {
                const pickerIframes = document.querySelectorAll('iframe[src*="picker"]');
                if (pickerIframes.length > 0) {
                    pickerIframes.forEach((iframe: Element) => {
                        const iframeEl = iframe as HTMLIFrameElement;
                        iframeEl.style.zIndex = '10001';
                        iframeEl.style.position = 'fixed';
                        iframeEl.style.top = '0';
                        iframeEl.style.left = '0';
                        iframeEl.style.width = '100%';
                        iframeEl.style.height = '100%';
                        iframeEl.style.border = 'none';
                        iframeEl.style.pointerEvents = 'auto';
                    });

                    const overlays = document.querySelectorAll('[data-radix-dialog-overlay]');
                    overlays.forEach((overlay: Element) => {
                        (overlay as HTMLElement).style.pointerEvents = 'none';
                        (overlay as HTMLElement).style.zIndex = '9998';
                    });

                    const dialogContents = document.querySelectorAll('[data-radix-dialog-content]');
                    dialogContents.forEach((content: Element) => {
                        (content as HTMLElement).style.pointerEvents = 'none';
                    });
                } else {
                    const overlays = document.querySelectorAll('[data-radix-dialog-overlay]');
                    overlays.forEach((overlay: Element) => {
                        (overlay as HTMLElement).style.pointerEvents = 'auto';
                        (overlay as HTMLElement).style.zIndex = '50';
                    });

                    const dialogContents = document.querySelectorAll('[data-radix-dialog-content]');
                    dialogContents.forEach((content: Element) => {
                        (content as HTMLElement).style.pointerEvents = 'auto';
                    });
                }
            };

            setTimeout(styleIframes, 100);

            const observer = new MutationObserver(() => {
                styleIframes();
            });

            observer.observe(document.body, {childList: true, subtree: true});

            setTimeout(() => {
                const checkIfClosed = setInterval(() => {
                    const pickerIframes = document.querySelectorAll('iframe[src*="picker"]');
                    if (pickerIframes.length === 0) {
                        styleIframes();
                        observer.disconnect();
                        clearInterval(checkIfClosed);
                    }
                }, 500);

                setTimeout(() => {
                    observer.disconnect();
                    clearInterval(checkIfClosed);
                }, 300000);
            }, 1000);
        } catch (err) {
            console.error(err);
            setError("Failed to build picker. Verify API Key and App ID.");
            if (onPickerStateChange) {
                onPickerStateChange(false);
            }
        }
    }, [pickerInited, accessToken, config.apiKey, config.appId, onPickerStateChange, listFolderContents, importFolderFiles, onFilesSelected, getFileBlob, extractTextOnly]);

    const handleSignOut = useCallback(() => {
        if (accessToken) {
            (window as any).google.accounts.oauth2.revoke(accessToken, () => {
                setAccessToken(null);
                setUserProfile(null);
                setSelectedFiles([]);
                clearToken();
                toast({title: "Signed Out", description: "Successfully disconnected from Google Drive."});
            });
        } else {
            setAccessToken(null);
            setUserProfile(null);
            clearToken();
        }
    }, [accessToken]);

    if (!accessToken) {
        if (children) {
            return (<>
                <div
                    ref={childrenRef}
                    onClick={handleAuthClick}
                    className="w-full"
                    style={{ pointerEvents: isLoading || !scriptsLoaded ? 'none' : 'auto', opacity: isLoading || !scriptsLoaded ? 0.6 : 1 }}
                >
                    {isLoading ? (
                        <div className="flex flex-row items-center ml-1.5 sm:ml-6">
                            <Loader2 className="h-4 w-4 mr-2 animate-spin"/>
                            <span className="text-sm sm:text-base">Connecting...</span>
                        </div>
                    ) : (
                        <>
                            {children}
                        </>
                    )}
                </div>
            </>)
        } else {
            return (
                <Card className="w-full">
                    {
                        !children ?
                            <CardHeader className="px-4 sm:px-6">
                                <CardTitle className="text-lg sm:text-xl">Connect Google Drive</CardTitle>
                                <CardDescription className="text-xs sm:text-sm">
                                    Connect your Google account to import files and folders from Google Drive
                                </CardDescription>
                            </CardHeader> :
                            null
                    }
                    <CardContent className="space-y-4 px-4 sm:px-6">
                        <Button
                            onClick={handleAuthClick}
                            disabled={isLoading || !scriptsLoaded}
                            className="w-full text-sm sm:text-base">
                            {isLoading ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin"/>
                                    Connecting...
                                </>
                            ) : (
                                <>
                                    <ExternalLink className="h-4 w-4 mr-2"/>
                                    Connect Google Drive
                                </>
                            )}
                        </Button>
                    </CardContent>
                </Card>
            );
        }
    }

    return (
        <>
            {children && (
                <div 
                    ref={childrenRef}
                    style={{ 
                        position: 'absolute', 
                        visibility: 'hidden', 
                        pointerEvents: 'none',
                        top: '-9999px',
                        left: '-9999px'
                    }}
                >
                    {children}
                </div>
            )}
            <Card 
                onClick={(e) => e.stopPropagation()} 
                className={children ? "" : "w-full max-w-full overflow-hidden box-border"}
                style={children && calculatedWidth ? { width: `${calculatedWidth}px` } : undefined}
            >
                {
                    !children ?
                        <CardHeader className="pb-3 px-2 sm:px-4 md:px-6 max-w-full overflow-hidden box-border">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 max-w-full">
                            <div className="flex-1 min-w-0 max-w-full overflow-hidden">
                                <CardTitle className="text-sm sm:text-lg md:text-xl truncate max-w-full">Import from Google Drive</CardTitle>
                                <CardDescription className="text-xs sm:text-sm mt-1 line-clamp-2 max-w-full">
                                    Import individual files or entire folders as reference sources
                                </CardDescription>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleSignOut}
                                className="text-muted-foreground self-start sm:self-auto text-xs sm:text-sm shrink-0"
                            >
                                <LogOut className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2 shrink-0"/>
                                <span className="truncate">Disconnect</span>
                            </Button>
                        </div>
                    </CardHeader> : null
            }
            <CardContent className="space-y-3 sm:space-y-4 px-2 sm:px-4 md:px-6 max-w-full overflow-hidden box-border" onClick={(e) => e.stopPropagation()}>
                <Tabs value={pickerMode} onValueChange={(v) => setPickerMode(v as 'files' | 'folder')} className="w-full max-w-full overflow-hidden">
                    <TabsList className="grid w-full max-w-full grid-cols-2 h-auto gap-1 box-border">
                        <TabsTrigger value="files" className="flex items-center justify-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs md:text-sm px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 min-w-0 max-w-full overflow-hidden">
                            <Files className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 flex-shrink-0"/>
                            <span className="truncate text-center max-w-full">{ children ? 'Files' : 'Files' }</span>
                        </TabsTrigger>
                        <TabsTrigger value="folder" className="flex items-center justify-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs md:text-sm px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 min-w-0 max-w-full overflow-hidden">
                            <FolderIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 flex-shrink-0"/>
                            <span className="truncate text-center max-w-full">{ children ? 'Folder' : 'Folder' }</span>
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="files" className="mt-3 sm:mt-4 max-w-full overflow-hidden">
                        <Button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleOpenPicker('files');
                            }}
                            className="w-full max-w-full text-xs sm:text-sm md:text-base px-2 sm:px-4 overflow-hidden"
                            disabled={!pickerInited || isLoading || isImporting}
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 animate-spin shrink-0"/>
                                    <span className="truncate">Loading...</span>
                                </>
                            ) : (
                                <>
                                    <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 shrink-0"/>
                                    <span className="truncate">Select Files</span>
                                </>
                            )}
                        </Button>
                        <p className="text-[10px] sm:text-xs md:text-sm text-muted-foreground mt-1.5 sm:mt-2 line-clamp-2 max-w-full overflow-hidden">
                            Pick individual documents, images, or PDFs to import
                        </p>
                    </TabsContent>

                    <TabsContent value="folder" className="mt-3 sm:mt-4 max-w-full overflow-hidden">
                        <Button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleOpenPicker('folder');
                            }}
                            className="w-full max-w-full text-xs sm:text-sm md:text-base px-2 sm:px-4 overflow-hidden"
                            disabled={!pickerInited || isLoading || isImporting}
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 animate-spin shrink-0"/>
                                    <span className="truncate">Scanning...</span>
                                </>
                            ) : (
                                <>
                                    <FolderOpen className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 shrink-0"/>
                                    <span className="truncate">Select Folder</span>
                                </>
                            )}
                        </Button>
                        <p className="text-[10px] sm:text-xs md:text-sm text-muted-foreground mt-1.5 sm:mt-2 line-clamp-2 max-w-full overflow-hidden">
                            Import all documents from a folder including subfolders
                        </p>
                    </TabsContent>
                </Tabs>

                {importProgress && (
                    <div className="space-y-1.5 sm:space-y-2 p-2 sm:p-3 md:p-4 bg-muted/50 rounded-lg max-w-full overflow-hidden box-border">
                        <div className="flex items-center justify-between text-[10px] sm:text-xs md:text-sm gap-2 max-w-full">
                            <span className="font-medium truncate min-w-0 max-w-full">
                                {extractTextOnly ? 'Extracting text from files...' : 'Importing files...'}
                            </span>
                            <span className="text-muted-foreground shrink-0">
                                {importProgress.completed} / {importProgress.total}
                            </span>
                        </div>
                        <Progress
                            value={(importProgress.completed / importProgress.total) * 100}
                            className="h-1.5 sm:h-2 max-w-full"
                        />
                        <p className="text-[10px] sm:text-xs md:text-sm text-muted-foreground truncate max-w-full">
                            {importProgress.current}
                        </p>
                        {importProgress.failed.length > 0 && (
                            <p className="text-[10px] sm:text-xs md:text-sm text-destructive truncate max-w-full">
                                Failed: {importProgress.failed.length} files
                            </p>
                        )}
                    </div>
                )}

                {selectedFiles.length > 0 && (
                    <div className="space-y-1.5 sm:space-y-2 max-w-full overflow-hidden box-border">
                        {isFinalConfirmation && (
                            <div className="flex items-center gap-2 p-2 sm:p-3 border rounded-lg bg-muted/30">
                                <Checkbox
                                    checked={isConfirmed}
                                    onCheckedChange={(checked) => setIsConfirmed(checked === true)}
                                    id="confirm-import"
                                />
                                <label
                                    htmlFor="confirm-import"
                                    className="text-[10px] sm:text-xs md:text-sm cursor-pointer flex-1"
                                >
                                    Confirm import of {selectedFiles.length} file(s)
                                </label>
                                {isConfirmed && (
                                    <Button
                                        size="sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (onFilesSelected) {
                                                if (extractTextOnly) {
                                                    // Return extracted text format
                                                    const extractedFiles = selectedFiles
                                                        .filter(f => f.isExtracted)
                                                        .map(f => ({
                                                            filename: f.filename,
                                                            text: f.text || '',
                                                            mimeType: f.mimeType || '',
                                                            folderPath: f.folderPath || '',
                                                        }));
                                                    onFilesSelected(extractedFiles);
                                                } else {
                                                    // Return uploaded file format
                                                    const uploadedFiles = selectedFiles
                                                        .filter(file => !file.isExtracted && file.id && file.url)
                                                        .map(file => ({
                                                            id: file.id!,
                                                            filename: file.filename,
                                                            url: file.url!,
                                                            folderPath: file.folderPath,
                                                        }));
                                                    onFilesSelected(uploadedFiles);
                                                }
                                            }
                                        }}
                                        className="text-[10px] sm:text-xs md:text-sm"
                                    >
                                        Confirm
                                    </Button>
                                )}
                            </div>
                        )}
                        <p className="text-[10px] sm:text-xs md:text-sm font-medium truncate max-w-full">
                            {extractTextOnly ? 'Extracted Files' : 'Imported Files'} ({selectedFiles.length}):
                        </p>
                        <div className="space-y-1.5 sm:space-y-2 max-h-40 sm:max-h-48 md:max-h-64 overflow-y-auto overflow-x-hidden -mr-1 pr-1 max-w-full box-border">
                            {selectedFiles.map((file, index) => {
                                const fileKey = file.id || file.filename || `file-${index}`;
                                return (
                                    <div
                                        key={fileKey}
                                        className="flex items-center justify-between p-1.5 sm:p-2 md:p-3 border rounded-lg gap-1.5 sm:gap-2 max-w-full overflow-hidden box-border"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0 max-w-full overflow-hidden">
                                            <FileText className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 text-muted-foreground flex-shrink-0"/>
                                            <div className="min-w-0 flex-1 max-w-full overflow-hidden">
                                                <span className="text-[10px] sm:text-xs md:text-sm truncate block max-w-full">{file.filename}</span>
                                                {file.folderPath && (
                                                    <span className="text-[9px] sm:text-[10px] md:text-xs text-muted-foreground truncate block max-w-full">
                                                        {file.folderPath}
                                                    </span>
                                                )}
                                                {file.isExtracted && file.text && (
                                                    <span className="text-[9px] sm:text-[10px] md:text-xs text-muted-foreground truncate block max-w-full">
                                                        {file.text.length} characters extracted
                                                    </span>
                                                )}
                                            </div>
                                            <CheckCircle2 className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0"/>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedFiles(prev => {
                                                    const remaining = file.id 
                                                        ? prev.filter(fileItem => fileItem.id !== file.id)
                                                        : prev.filter((_item, i) => i !== index);
                                                    if (onFilesSelected) {
                                                        if (extractTextOnly) {
                                                            // Return extracted text format
                                                            const extractedFiles = remaining
                                                                .filter(f => f.isExtracted)
                                                                .map(f => ({
                                                                    filename: f.filename,
                                                                    text: f.text || '',
                                                                    mimeType: f.mimeType || '',
                                                                    folderPath: f.folderPath || '',
                                                                }));
                                                            onFilesSelected(extractedFiles);
                                                        } else {
                                                            // Return uploaded file format
                                                            const uploadedFiles = remaining
                                                                .filter(file => !file.isExtracted && file.id && file.url)
                                                                .map(file => ({
                                                                    id: file.id!,
                                                                    filename: file.filename,
                                                                    url: file.url!,
                                                                    folderPath: file.folderPath,
                                                                }));
                                                            onFilesSelected(uploadedFiles);
                                                        }
                                                    }
                                                    return remaining;
                                                });
                                            }}
                                            className="flex-shrink-0 h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 p-0"
                                        >
                                            <XCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4"/>
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {error && (
                    <div className="flex items-start sm:items-center gap-1.5 sm:gap-2 p-2 sm:p-3 md:p-4 bg-destructive/10 text-destructive rounded-lg max-w-full overflow-hidden box-border">
                        <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 mt-0.5 sm:mt-0"/>
                        <p className="text-[10px] sm:text-xs md:text-sm break-words min-w-0 max-w-full overflow-hidden">{error}</p>
                    </div>
                )}
            </CardContent>
        </Card>
        </>
    );
});
